/**
 * Fetching a document's server-rendered thumbnail, with the retry the server
 * expects.
 *
 * The retry is not an optimisation — it is part of the contract.
 * `thumbnail_routes.py` answers **503 with a Retry-After** in two situations
 * that both resolve on their own:
 *
 *  * the graph's entity extraction has not committed nodes yet, so the
 *    annotated render would have no annotations on it (FS-482), and
 *  * a transient failure during generation.
 *
 * A caller that treats the first answer as final therefore reports "no view for
 * this file" about a drawing whose view is seconds away. The board learned that
 * the hard way; the preview overlay had its own un-retried copy and reported
 * exactly that, which is why "Detected" read as empty.
 */
import { apiService } from '@/service/api'

/** Only these mean "not yet". Anything else is an answer. */
export const THUMBNAIL_RETRYABLE = new Set([502, 503])
export const THUMBNAIL_MAX_ATTEMPTS = 3
export const THUMBNAIL_FALLBACK_RETRY_DELAY_MS = 1500

/**
 * @param beforeAttempt Run before each attempt; THROW from it to abandon the
 *   ladder. Lets a caller whose view has gone away stop waiting without this
 *   module knowing anything about its lifecycle.
 */
export async function fetchThumbnailWithRetry(
  docId: string,
  beforeAttempt?: () => void,
): Promise<Blob> {
  for (let attempt = 1; ; attempt++) {
    beforeAttempt?.()
    try {
      const { blob } = await apiService.getDocumentThumbnail(docId)
      return blob
    } catch (err) {
      // The shape `fetchOrError` attaches to its rejection (api.ts). Anything
      // without it — a network abort, a thrown string — has no status and falls
      // through to the throw: only a status we recognise as "not yet" retries.
      const response = (err as { response?: { status?: number; retryAfterSeconds?: number } })
        ?.response
      const status = response?.status ?? 0
      if (!THUMBNAIL_RETRYABLE.has(status) || attempt >= THUMBNAIL_MAX_ATTEMPTS) throw err
      // Cadence from the server's own Retry-After so it cannot drift from the
      // backend's RETRY_AFTER_SECONDS, backing off linearly so a second failure
      // waits longer than the first.
      const base =
        response?.retryAfterSeconds !== undefined
          ? response.retryAfterSeconds * 1000
          : THUMBNAIL_FALLBACK_RETRY_DELAY_MS
      await new Promise((resolve) => setTimeout(resolve, base * attempt))
    }
  }
}
